import React, { useState } from "react";
import { Check, X, AlertCircle, ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import FormConsent from "../components/site/FormConsent";
import {
  FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import { getCtaVariant } from "../lib/ab";

const QUESTIONS = [
  { id: "all-photos", q: "Does every dish on your menu have a photo?", hint: "Customers skip un-photographed items 40% more often." },
  { id: "square-crop", q: "Are your photos square-cropped (1:1)?", hint: "Uber Eats / DoorDash thumbnails are square. Landscape photos get auto-cropped badly." },
  { id: "consistent-light", q: "Do all photos share the same lighting and white-balance?", hint: "Inconsistent photos make a menu look unprofessional — and lower average order value." },
  { id: "hero-quality", q: "Is your top hero photo professional-grade (not phone)?", hint: "The first photo decides whether someone scrolls further. Phone shots cost orders." },
  { id: "no-stock", q: "Are all photos of YOUR food (not stock images)?", hint: "Customers spot stock photos and tap away. Trust drops, refunds rise." },
  { id: "recent", q: "Were the photos taken in the last 12 months?", hint: "Old menu photos signal a stale venue. Algorithms reward freshness." },
];

const GRADE = (score) => {
  if (score === 6) return { label: "A+", title: "World-class.", color: "text-ember", desc: "Your delivery menu is in the top 5% of Melbourne hospitality. Don't change a thing — keep refreshing quarterly." };
  if (score >= 5) return { label: "A", title: "Strong.", color: "text-ember", desc: "Your menu performs above average. One or two small tweaks would push you into the top tier." };
  if (score >= 4) return { label: "B", title: "Decent.", color: "text-bone", desc: "Above average, but you're leaving orders on the table. Two or three reshoots would meaningfully lift conversion." };
  if (score >= 2) return { label: "C", title: "Underperforming.", color: "text-bone/85", desc: "Your menu is costing you orders every day. A half-day refresh shoot would pay for itself in 4–6 weeks." };
  return { label: "D", title: "Urgent attention needed.", color: "text-ember", desc: "Your delivery menu is significantly underperforming. A full menu reshoot is one of the highest-ROI moves you can make right now." };
};

export default function DeliveryAppPhotoAudit() {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [lead, setLead] = useState({ name: "", restaurant: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const score = QUESTIONS.reduce((acc, q) => acc + (answers[q.id] === "yes" ? 1 : 0), 0);
  const answered = QUESTIONS.every((q) => answers[q.id]);
  const grade = GRADE(score);

  const setAns = (id, v) => setAnswers((s) => ({ ...s, [id]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const variant = getCtaVariant();
      const answerSummary = QUESTIONS.map((q) => `${q.q} → ${answers[q.id] || "—"}`).join("\n");
      await fetch(`${API}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...lead,
          source: "delivery-app-photo-audit",
          message: `Audit score: ${score}/6 (${grade.label})\n\n${answerSummary}`,
          cta_variant: variant.id,
        }),
      });
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-12 lg:pb-16 overflow-hidden bg-ink">
        <div className="absolute inset-0 opacity-30"><Photo slug="menu-butter-chicken" alt="" ratio="auto" className="!relative h-full w-full" /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink" />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <Breadcrumb current="Delivery App Photo Audit" />
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">Free audit · 90 seconds · Instant grade</p>
          <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            How well does
            <br />
            your <span className="italic font-light text-ember">delivery menu sell?</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-bone/80">
            Six honest yes/no questions about your Uber Eats menu. We score it
            from A+ to D, then email you a deeper one-page audit if you&apos;d like
            one.
          </p>
        </div>
      </section>

      {/* What we audit */}
      <section className="bg-bone text-ink py-16 lg:py-20 border-b border-ink/10">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">What this audit checks</p>
          <h2 className="font-display tracking-editorial leading-[0.95] mb-6" style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
            We check your current Uber Eats, DoorDash, or Menulog listing against platform best practice.
          </h2>
          <p className="text-char text-base md:text-lg leading-relaxed mb-6 max-w-3xl">
            Four signals decide whether a thumbnail wins or loses an order on a customer&apos;s phone:
          </p>
          <ul className="space-y-2.5 text-base text-char">
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Image resolution.</strong> Uber Eats requires 800×800px square minimum; below that, the platform compresses your photo into a soft, low-contrast thumbnail.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Background clarity.</strong> White or off-white outperforms patterned wood, marble or busy table surfaces every time at 100×100px display size.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Dish visibility at thumbnail size.</strong> If the dish isn&apos;t recognisable when zoomed out to 100×100px, customers scroll past without tapping.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Scroll-stop rating.</strong> Does the hero photo at the top of each category compel a tap, or does the customer scroll straight through to the next restaurant?</span></li>
          </ul>
        </div>
      </section>

      {/* Audit body */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          {!revealed && (
            <>
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Six questions</p>
              <h2 className="font-display tracking-editorial leading-[0.95] mb-12" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                Be honest.<br /><span className="italic font-light text-ember">It only takes 90 seconds.</span>
              </h2>

              <div className="space-y-7">
                {QUESTIONS.map((q, i) => (
                  <div key={q.id} className="border-b border-ink/15 pb-7" data-testid={`audit-q-${q.id}`}>
                    <div className="flex items-baseline gap-4 mb-3">
                      <span className="font-sans font-bold text-sm text-ember tracking-tight shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-display text-lg md:text-xl tracking-editorial leading-tight">{q.q}</h3>
                    </div>
                    <p className="text-sm text-char-mute italic ml-9 mb-4">{q.hint}</p>
                    <div className="flex gap-3 ml-9">
                      {["yes", "no"].map((v) => (
                        <button
                          key={v}
                          type="button"
                          data-testid={`audit-${q.id}-${v}`}
                          onClick={() => setAns(q.id, v)}
                          className={`px-5 py-2 text-xs uppercase tracking-wider2 font-bold border transition-all ${
                            answers[q.id] === v
                              ? (v === "yes" ? "border-ember bg-ember text-bone" : "border-ink bg-ink text-bone")
                              : "border-ink/15 text-char hover:border-ink"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-char-mute">
                  {answered ? "All questions answered." : `${Object.keys(answers).length} of ${QUESTIONS.length} answered.`}
                </p>
                <button
                  type="button"
                  disabled={!answered}
                  onClick={() => setRevealed(true)}
                  data-testid="audit-reveal"
                  className="inline-flex items-center gap-2 bg-ember text-bone font-sans font-bold uppercase tracking-wider2 text-xs px-7 py-4 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ember-deep transition-colors"
                >
                  Reveal My Grade <ArrowUpRight size={14} strokeWidth={2} />
                </button>
              </div>
            </>
          )}

          {revealed && (
            <div data-testid="audit-result">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Your delivery menu grade</p>
              <div className="flex flex-col md:flex-row md:items-end gap-8 md:gap-12 mb-12">
                <p className={`font-display font-bold leading-none ${grade.color}`} style={{ fontSize: "clamp(6rem, 18vw, 14rem)" }}>{grade.label}</p>
                <div className="flex-1">
                  <h2 className="font-display tracking-editorial leading-[0.95] mb-5" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                    {grade.title}
                  </h2>
                  <p className="text-base md:text-lg text-char leading-relaxed">{grade.desc}</p>
                  <p className="mt-4 text-xs uppercase tracking-wider2 text-ember font-bold">Score: {score} of {QUESTIONS.length}</p>
                </div>
              </div>

              {/* Per-question summary */}
              <div className="border-t border-ink/15 pt-10 mb-12">
                <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-6">Breakdown</p>
                <ul className="space-y-3">
                  {QUESTIONS.map((q) => (
                    <li key={q.id} className="flex items-start gap-4 text-sm md:text-base">
                      {answers[q.id] === "yes"
                        ? <Check size={18} className="text-ember mt-0.5 shrink-0" />
                        : <X size={18} className="text-ink/40 mt-0.5 shrink-0" />}
                      <span className={answers[q.id] === "yes" ? "text-char" : "text-char/55 line-through"}>{q.q}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Email gate / lead capture */}
              {!sent ? (
                <form onSubmit={onSubmit} className="bg-bone border border-ink/10 p-7 md:p-10">
                  <div className="flex items-center gap-3 mb-5">
                    <AlertCircle size={18} strokeWidth={1.8} className="text-ember" />
                    <p className="text-xs uppercase tracking-wider2 text-ember font-bold">Get the deeper audit</p>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-3">
                    Want a personalised, one-page audit?
                  </h3>
                  <p className="text-sm md:text-base text-char-mute leading-relaxed mb-8 max-w-2xl">
                    We&apos;ll review your actual Uber Eats menu within one business day
                    and send back specific recommendations — which dishes to reshoot
                    first, what the lift looks like, and a no-obligation quote.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                    {[
                      ["name", "Your name", "text"],
                      ["restaurant", "Venue name", "text"],
                      ["email", "Email *", "email"],
                      ["phone", "Phone *", "tel"],
                    ].map(([k, label, type]) => (
                      <label key={k} className="block">
                        <span className="text-xs uppercase tracking-wider2 font-bold text-char/70 block mb-2">{label}</span>
                        <input
                          type={type}
                          required={k === "email" || k === "phone" || k === "name" || k === "restaurant"}
                          data-testid={`audit-lead-${k}`}
                          value={lead[k]}
                          onChange={(e) => setLead((s) => ({ ...s, [k]: e.target.value }))}
                          className="w-full bg-transparent border-b border-ink/15 focus:border-ember text-ink font-display text-xl tracking-editorial pb-2 focus:outline-none transition-colors"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                    <p className="text-xs text-char-mute max-w-sm leading-relaxed">No spam. One personalised email reply, then a phone call if you want one.</p>
                    <CTAButton type="submit" variant="primary" size="md" disabled={sending}>{sending ? "Sending…" : "Send Me The Audit"}</CTAButton>
                  </div>
                  <FormConsent className="mt-6" />
                </form>
              ) : (
                <div data-testid="audit-sent" className="bg-bone border border-ember/30 p-7 md:p-10">
                  <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Audit request received</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-3">
                    Thanks — we&apos;ll send your personalised audit within one business day.
                  </h3>
                  <p className="text-sm text-char-mute leading-relaxed">
                    If your delivery menu launch is sooner, call <a href="tel:0494756111" className="text-ember">0494 756 111</a> directly.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <SisterServices keys={["menu", "ubereats", "food", "gbp"]} />
      <FAQSection
        eyebrow="Common questions · Delivery menu audits"
        headline="Audit, scoring,"
        italic="and follow-up."
        intro="The audit is free and anonymous unless you choose to share your details for the personalised follow-up."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Skip the audit · Just talk to us"
        headline="Or call"
        italic="directly."
        intro="Most delivery menu refreshes are scoped in under five minutes by phone."
        backdropSlug="menu-veg-crispy"
      />
      <Footer />
    </div>
  );
}

const FAQS = [
  { q: "Is the audit accurate?", a: "It's intentionally simple — six honest questions get you to roughly the right grade in 90 seconds. The personalised follow-up (if you request it) is much deeper and reviews your actual menu." },
  { q: "Why does Uber Eats photo quality matter so much?", a: "Delivery apps grade menus by image quality and update rank order based on it. Venues with strong photography see 20–40% higher add-to-cart on the dishes that get reshot." },
  { q: "How fast can a delivery menu refresh happen?", a: "Most refresh shoots happen 7–14 days from the first call. Urgent (72-hour) turnarounds are available — mention the deadline on the call." },
];
